import { Component, signal, Signal, computed } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { AppointmentService } from '../../services/appointment.service';
import { PatientService } from '../../services/patient.service';
import { Appointment } from '../../models/appointment.model';
import { Patient } from '../../models/patient.model';
import { ButtonComponent, InputComponent, DateComponent } from '../ui';

@Component({
  selector: 'app-appointments',
  imports: [CommonModule, FormsModule, DatePipe, RouterLink, ButtonComponent, InputComponent, DateComponent],
  templateUrl: './appointments.component.html',
  styleUrl: './appointments.component.css'
})
export class AppointmentsComponent {
  appointments!: Signal<Appointment[]>;
  patients!: Signal<Patient[]>;
  sortedAppointments = computed(() => [...this.appointments()].sort((a, b) => {
    const aDate = new Date(a.date);
    const bDate = new Date(b.date);
    if (a.time) {
      const [h, m] = a.time.split(':').map(Number);
      aDate.setHours(h ?? 0, m ?? 0, 0, 0);
    }
    if (b.time) {
      const [h, m] = b.time.split(':').map(Number);
      bDate.setHours(h ?? 0, m ?? 0, 0, 0);
    }
    return aDate.getTime() - bDate.getTime();
  }));
  showModal = signal(false);
  editingAppointment = signal<Appointment | null>(null);
  appointmentForm = signal<Partial<Appointment>>({});

  constructor(
    private appointmentService: AppointmentService,
    private patientService: PatientService
  ) {
    this.appointments = this.appointmentService.getAppointments();
    this.patients = this.patientService.getPatients();
    this.appointmentService.startAutoQueueUpdates();
  }

  openAddModal() {
    this.editingAppointment.set(null);
    this.appointmentForm.set({
      patientId: '',
      patientName: '',
      doctorId: '1',
      doctorName: 'Dr. Sarah Williams',
      date: new Date(),
      time: '09:00',
      duration: 30,
      type: 'Consultation',
      status: 'Scheduled',
      reason: ''
    });
    this.showModal.set(true);
  }

  openEditModal(appointment: Appointment) {
    this.editingAppointment.set(appointment);
    this.appointmentForm.set({ ...appointment });
    this.showModal.set(true);
  }

  closeModal() {
    this.showModal.set(false);
    this.editingAppointment.set(null);
  }

  onPatientSelect(patientId: string) {
    const patient = this.patients().find((p: Patient) => p.id === patientId);
    if (patient) {
      this.appointmentForm().patientName = `${patient.firstName} ${patient.lastName}`;
    }
  }

  onDateChange(value: string | Date | null) {
    if (value) {
      if (typeof value === 'string') {
        this.appointmentForm().date = new Date(value);
      } else if (value instanceof Date) {
        this.appointmentForm().date = value;
      }
    }
  }

  saveAppointment() {
    const form = this.appointmentForm();
    if (this.editingAppointment()) {
      this.appointmentService.updateAppointment(this.editingAppointment()!.id, form);
    } else {
      this.appointmentService.addAppointment(form as Omit<Appointment, 'id' | 'createdAt' | 'updatedAt'>);
    }
    this.closeModal();
  }

  deleteAppointment(id: string) {
    if (confirm('Are you sure you want to delete this appointment?')) {
      this.appointmentService.deleteAppointment(id);
    }
  }

  updateStatus(id: string, status: Appointment['status']) {
    this.appointmentService.updateAppointment(id, { status });
  }
}

